import { createContext, useState, useEffect } from "react";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { initKey, initModel, initVar } from "./initData";
import { SharedStateEvent, updateSharedState } from "src/utils/shared-state";
import { SAFE_getCmap } from "src/cmaps/cmaps";

interface DataContextType {
  key: string;
  setKey: (key: string) => void;
  isLoadingingKeys: boolean;
  model: string;
  setModel: (model: string) => void;
  data: any[];
  isLoadingData: boolean;
  availableKeys: string[];
  selectedVar: string;
  setSelectedVar: (selectedVar: string) => void;
  window: [string, string] | [];
  setWindow: (window: [string, string]) => void;
  cmap: string;
  edfWindow: [string, string] | [];
  setEDFWindow: (edfWindow: [string, string]) => void;
  channels: string[];
  isLoadingChannels: boolean;
  selectedChannel: string;
  setSelectedChannel: (prevValue: string) => void;
  dataWindow: [string, string] | [];
  setDataWindow: (dataWindow: [string, string]) => void;
}

const DataContext = createContext<DataContextType>({
  key: "",
  setKey: () => {},
  isLoadingingKeys: false,
  model: "",
  setModel: () => {},
  data: [],
  isLoadingData: false,
  availableKeys: [],
  selectedVar: "",
  setSelectedVar: () => {},
  window: [],
  setWindow: () => {},
  cmap: "",
  edfWindow: [],
  setEDFWindow: () => {},
  channels: [],
  isLoadingChannels: false,
  selectedChannel: "",
  setSelectedChannel: () => {},
  dataWindow: [],
  setDataWindow: () => {},
});

const fetchSeals = async () => {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}seals/`);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

export const defaultFineSampleRate = 1;
export const defaultCoarseSampleRate = 50;

// TODO:
// - 30 seconds before and after the playhead - line graph
// - 30 minutes before and after the playhead - spectrogram
const fetchSealData = async ({
  key,
  start,
  end,
  sampleRate: providedSampleRate,
}: {
  key: string;
  start: string;
  end: string;
  sampleRate?: number;
}) => {
  const sampleRate =
    providedSampleRate ??
    (start && end ? defaultFineSampleRate : defaultCoarseSampleRate);
  const startString = start ? `&start_time=${start}` : "";
  const endString = end ? `&end_time=${end}` : "";
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}seal/${key}?sample_rate=${sampleRate}${startString}${endString}`
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  const res = await response.json();
  const transformRes = res.map((d) => {
    d.time = new Date(d.R_Time).getTime();
    return d;
  });
  const t = transformRes.sort((a, b) => {
    if (a.time < b.time) return -1;
    if (a.time > b.time) return 1;
    return 0;
  });
  return t.map((d, idx) => {
    d["idx"] = idx;
    d["sample_rate"] = sampleRate;
    return d;
  });
};

const fetchChannels = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}edf/seal/channels`
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  const json = await response.json();
  return json.channels;
};

// Creating a provider component
const DataProvider = ({ children }) => {
  const [model, setModel] = useState(initModel);
  const [selectedVar, setSelectedVar] = useState(initVar);
  const [window, setWindow] = useState<[string, string] | []>([]);
  const [dataWindow, setDataWindow] = useState<[string, string] | []>([]);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [cmap, setCmap] = useState();
  const [edfWindow, setEDFWindow] = useState<[string, string] | []>([]);

  const { data: availableKeys, isLoading: isLoadingingKeys } = useQuery({
    queryKey: ["availableKeys"],
    queryFn: fetchSeals,
    initialData: [],
  });

  const [key, setKey] = useState(initKey);
  const { data, isLoading: isLoadingData } = useQuery({
    queryKey: ["sealData", key, ...dataWindow],
    queryFn: () =>
      fetchSealData({ key, start: dataWindow[0], end: dataWindow[1] }),
    enabled: !!key,
    initialData: [],
  });

  const { data: channels, isLoading: isLoadingChannels } = useQuery({
    queryKey: ["channels"],
    queryFn: fetchChannels,
    initialData: [],
  });

  useEffect(() => {
    setCmap(SAFE_getCmap(data, selectedVar));
  }, [data, selectedVar]);

  // Set the time window of the data
  useEffect(() => {
    if (!data?.length) return;
    setWindow([data[0].R_Time, data[data.length - 1].R_Time]);
  }, [data]);

  // Effect to update shared state whenever cmap changes
  useEffect(() => {
    updateSharedState(SharedStateEvent.CMAP_UPDATE, {
      cmap,
      selectedVar,
      data,
    });
  }, [cmap, selectedVar, data]);

  // Automatically select the first key if the current key is not in the available keys
  useEffect(() => {
    if (!availableKeys.length || availableKeys.includes(key)) return;
    setKey(availableKeys[0]);
  }, [availableKeys, key]);

  useEffect(() => {
    if (!window.length || !data.length) return;
    updateSharedState(SharedStateEvent.WINDOW_UPDATE, { window });
    const newIdx =
      dataWindow[0] && dataWindow[1]
        ? data.find((d) => dayjs(dataWindow[0]).diff(d.time, "second") < 10)
            .idx + 100
        : data.find(
            (d) => Math.abs(dayjs(d.time).diff(dayjs(window[0]), "second")) < 65
          ).idx;
    updateSharedState(SharedStateEvent.IDX_UPDATE, { idx: newIdx });
  }, [data, dataWindow, window]);

  return (
    <DataContext.Provider
      value={{
        key,
        setKey,
        isLoadingingKeys,
        model,
        setModel,
        data,
        isLoadingData,
        availableKeys,
        selectedVar,
        setSelectedVar,
        window,
        setWindow,
        cmap,
        edfWindow,
        setEDFWindow,
        channels,
        isLoadingChannels,
        selectedChannel,
        setSelectedChannel,
        dataWindow,
        setDataWindow,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export { DataContext, DataProvider };
